// src/pages/VehicleV2/WorkLocationListing.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaTrash, FaChevronDown, FaChevronUp } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/workLocationListing";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const WorkLocationListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);

  const pages = useMemo(() => Math.max(1, Math.ceil((total || 0) / (limit || 10))), [total, limit]);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const fetchList = useCallback(
    async (opts = {}) => {
      const nextPage = opts.page ?? page;
      const nextLimit = opts.limit ?? limit;
      const nextSearch = opts.search ?? search;
      try {
        if (!toInt(branchId)) {
          toast.error("branchid not found in localStorage");
          setRows([]);
          setTotal(0);
          return;
        }
        setLoading(true);
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            branchId: toInt(branchId, 0),
            page: nextPage,
            limit: nextLimit,
            search: toStr(nextSearch),
            loginUserId,
          },
          headers: commonHeaders,
        });
        if (!data?.success) throw new Error(data?.message || "Failed to load work locations");
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(toInt(data?.total, 0));
        setSelectedIds([]);
        setPage(nextPage);
        setLimit(nextLimit);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to load work locations");
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [branchId, page, limit, search, loginUserId, commonHeaders]
  );

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  const handleDelete = async () => {
    if (!selectedIds.length) return toast.warn("Please select at least one location to delete");
    if (!window.confirm(`Delete ${selectedIds.length} selected record(s)?`)) return;
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/delete`,
        { ids: selectedIds, loginUserId, branchId: toInt(branchId, 0), branchType: branch },
        { headers: commonHeaders }
      );
      if (!data?.success && !(Array.isArray(data?.deleted) && data.deleted.length > 0)) {
        throw new Error(data?.message || "Delete failed");
      }
      toast.success(data?.message || "Deleted Successfully");
      const nextPage = rows.length === selectedIds.length && page > 1 ? page - 1 : page;
      await fetchList({ page: nextPage });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(r.wlid));

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">WORK LOCATION</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Work Location Listing
            </h2>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate("/vehicle/WorkLocationMaster")}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
              >
                <FaPlus /> Add New
              </button>
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Search" : "Show Search"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSearch("");
                      fetchList({ page: 1, search: "" });
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => fetchList({ page: 1, search })}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch /> Search
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass}>Search</label>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchList({ page: 1, search })}
                  className={inputClass}
                  placeholder="Search by name / code / address / status..."
                />
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>Loading...</div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800 text-gray-400" : "bg-gray-50 text-gray-500"}`}>
                No Work Locations Found
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      {["S.No", "Name", "Code", "Address1", "Address2", "Status"].map((h) => (
                        <th key={h} className="px-6 py-4 text-left text-sm font-semibold">{h}</th>
                      ))}
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => setSelectedIds(allSelected ? [] : rows.map((r) => r.wlid))}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((r, idx) => (
                      <tr
                        key={r.wlid}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className="px-6 py-4 text-sm">{(page - 1) * limit + idx + 1}</td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {r.wlname}
                        </td>
                        <td className="px-6 py-4 text-sm">{r.wlcode}</td>
                        <td className="px-6 py-4 text-sm">{r.addr1}</td>
                        <td className="px-6 py-4 text-sm">{r.addr2}</td>
                        <td className="px-6 py-4 text-sm">{r.status}</td>
                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => navigate(`/vehicle/WorkLocationMaster?worklocationid=${r.wlid}`)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg"
                          >
                            ✏️
                          </button>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.wlid)}
                            onChange={() =>
                              setSelectedIds((p) =>
                                p.includes(r.wlid) ? p.filter((x) => x !== r.wlid) : [...p, r.wlid]
                              )
                            }
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-4">
                  <button
                    onClick={handleDelete}
                    disabled={!selectedIds.length}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow text-white ${
                      selectedIds.length === 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700"
                    }`}
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Page <b>{page}</b> of <b>{pages}</b> | Total: <b>{total}</b>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={limit}
                onChange={(e) => fetchList({ page: 1, limit: toInt(e.target.value, 10) })}
                className={inputClass.replace("w-full ", "w-auto ")}
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
              <button
                disabled={page <= 1}
                onClick={() => fetchList({ page: page - 1 })}
                className={`px-3 py-2 border rounded-lg ${page <= 1 ? "opacity-50 cursor-not-allowed" : ""} ${
                  isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"
                }`}
              >
                Prev
              </button>
              <button
                disabled={page >= pages}
                onClick={() => fetchList({ page: page + 1 })}
                className={`px-3 py-2 border rounded-lg ${page >= pages ? "opacity-50 cursor-not-allowed" : ""} ${
                  isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default WorkLocationListing;